import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AddOrganization from "../organization/AddOrganization";
import {
  extractOrganizationId,
  extractOrganizationName,
  fetchOrganizationById,
} from "../organization/organizationApi";
import EatsAdminLayout from "./EatsAdminLayout";
import EatsAdminCreateStepper from "./EatsAdminCreateStepper";
import EatsAdminPageHeader from "./EatsAdminPageHeader";
import RootAdminAssignPanel from "./RootAdminAssignPanel";
import "./EatsAdminCreatePage.css";

function toActiveOrganization(organization, fallbackId = null) {
  const organizationId = extractOrganizationId(organization) || fallbackId;
  if (!organizationId) return null;
  return {
    organizationId,
    organizationName:
      extractOrganizationName(organization) || organization?.organizationName || "",
    organizationCode: organization?.organizationCode || organization?.Organization_Code || "",
  };
}

const EatsAdminCreateOrganizationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const currentUserId = currentUser?.userId || null;

  const [tab, setTab] = useState("company");
  const [activeOrganization, setActiveOrganization] = useState(null);

  useEffect(() => {
    const requestedId = searchParams.get("organizationId");
    if (!requestedId) {
      return undefined;
    }

    let cancelled = false;
    const loadOrganization = async () => {
      try {
        const organization = await fetchOrganizationById(requestedId);
        if (cancelled) return;
        const next = toActiveOrganization(organization, requestedId);
        if (!next) {
          setActiveOrganization(null);
          setTab("company");
          return;
        }
        setActiveOrganization(next);
        setTab("root-admin");
      } catch (error) {
        console.error(
          "Failed loading organization:",
          error?.response?.data || error?.message
        );
        if (!cancelled) {
          setActiveOrganization(null);
          setTab("company");
        }
      }
    };
    loadOrganization();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const handleOrganizationCreated = (result) => {
    const organization = result?.organization || result || {};
    const next = toActiveOrganization(organization);
    if (!next) {
      alert("Organization created but organizationId was not returned. Please retry.");
      return;
    }
    setActiveOrganization(next);
    setTab("root-admin");
    navigate(`/eats/admin/organizations/create?organizationId=${next.organizationId}`, {
      replace: true,
    });
  };

  return (
    <EatsAdminLayout activeTab="organizations">
      <div className="eats-admin-create-page">
        <EatsAdminPageHeader
          title="Create Organization"
          subtitle="Create organization first, then assign root admin user."
          backTo="/eats/admin?tab=organizations"
        />

        <EatsAdminCreateStepper
          activeStepId={tab}
          onStepChange={(next) => {
            if (next === "root-admin" && !activeOrganization?.organizationId) return;
            setTab(next);
          }}
          steps={[
            { id: "company", label: "Business" },
            {
              id: "root-admin",
              label: "Create Root Admin",
              disabled: !activeOrganization?.organizationId,
            },
          ]}
        />

        <div className="eats-admin-panel">
          {tab === "company" ? (
            <AddOrganization
              embedded
              createdById={currentUserId}
              onSuccess={handleOrganizationCreated}
              onClose={() => {}}
            />
          ) : activeOrganization ? (
            <RootAdminAssignPanel
              entityType="organization"
              entityId={activeOrganization.organizationId}
              entityName={activeOrganization.organizationName}
              createdById={currentUserId}
              title={`Create Owner for ${
                activeOrganization.organizationName ||
                `Organization ${activeOrganization.organizationId}`
              }`}
              onSuccess={() => navigate("/eats/admin?tab=organizations")}
            />
          ) : (
            <div className="eats-admin-empty-state">Please create an organization first.</div>
          )}
        </div>
      </div>
    </EatsAdminLayout>
  );
};

export default EatsAdminCreateOrganizationPage;
